import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  useDeleteNotificationMutation,
  usePublishNotificationMutation,
  useGetNotificationByIdQuery,
} from "./api/notificationApi";
import { showToast } from "../../core/utils/alert";
import { all_routes } from "../router/all_routes";

interface NotificationModalsProps {
  NotificationId?: string;
  onSuccess?: () => void;
  selectedAttachment?: string | string[];
  onDeleteAttachment?: (filename: string | string[]) => Promise<void>;
  isDeletingAttachment?: boolean;
  isDeleteBulk?: boolean;
  deleteType?: "notification" | "attachment";
}

const NotificationModals: React.FC<NotificationModalsProps> = ({
  NotificationId,
  onSuccess,
  selectedAttachment,
  onDeleteAttachment,
  isDeletingAttachment,
  isDeleteBulk,
  deleteType = "notification",
}) => {
  const [deleteNotification, { isLoading: isDeleting }] =
    useDeleteNotificationMutation();
  const [publishNotification, { isLoading: isPublishing }] =
    usePublishNotificationMutation();
  const navigate = useNavigate();

  // Fetch notification detail for feedback modal
  const { data: notificationData, isLoading: isLoadingFeedback } =
    useGetNotificationByIdQuery(
      { id: NotificationId || "" },
      { skip: !NotificationId }
    );

  const notification = notificationData?.data?.notification;

  // Combined handle delete function
// ... (rest of the handle functions remain same)
  const handleDelete = async (e: React.FormEvent) => {
    e.preventDefault();

    if (deleteType === "notification") {
      if (!NotificationId) return;
      try {
        const result: any = await deleteNotification({
          id: NotificationId,
        }).unwrap();
        if (result.success) {
          showToast({
            title: "Success",
            text: result.message || "Notification deleted successfully",
            icon: "success",
          });

          navigate(all_routes.notificationList);
        } else {
          showToast({
            title: "Error",
            text: result.error || "Could not delete the notification",
            icon: "error",
          });
        }
      } catch (error: any) {
        showToast({
          title: "Error",
          text: error.message || "Could not delete the notification",
          icon: "error",
        });
      }
    } else if (
      deleteType === "attachment" &&
      selectedAttachment &&
      onDeleteAttachment
    ) {
      await onDeleteAttachment(selectedAttachment);
    }
  };

  // Get modal content based on delete type
  const getDeleteModalContent = () => {
    if (deleteType === "notification") {
      return {
        title: "Delete Notification",
        message: "You want to delete the notification.",
        loading: isDeleting,
        buttonText: "Delete Notification",
      };
    } else {
      return {
        title: `Delete ${isDeleteBulk ? "Attachments" : "Attachment"}`,
        message: isDeleteBulk
          ? `Are you sure you want to delete ${
              Array.isArray(selectedAttachment) ? selectedAttachment.length : 0
            } selected attachments?`
          : `Are you sure you want to delete this attachment?`,
        loading: isDeletingAttachment,
        buttonText: `Delete ${isDeleteBulk ? "Selected" : "Attachment"}`,
      };
    }
  };

  const modalContent = getDeleteModalContent();

  // Handle publish notification
  const handlePublish = async () => {
    if (!NotificationId) return;

    try {
      const result: any = await publishNotification({
        id: NotificationId,
        isPublished: true,
      }).unwrap();
      if (result.success) {
        showToast({
          title: "Success",
          text: result.message || "Notification published successfully",
          icon: "success",
        });
        if (onSuccess) onSuccess();
      } else {
        showToast({
          title: "Error",
          text: result.error || "Could not publish the notification",
          icon: "error",
        });
        console.error(result.error);
      }
    } catch (error: any) {
      showToast({
        title: "Error",
        text: error.message || "Could not publish the notification",
        icon: "error",
      });
      console.error("Error publishing notification:", error);
    }
  };

  // Handle unpublish notification
  const handleUnpublish = async () => {
    if (!NotificationId) return;

    try {
      const result: any = await publishNotification({
        id: NotificationId,
        isPublished: false,
      }).unwrap();
      if (result.success) {
        showToast({
          title: "Success",
          text: result.message || "Notification unpublished successfully",
          icon: "success",
        });
        if (onSuccess) onSuccess();
      } else {
        showToast({
          title: "Error",
          text: result.error || "Could not unpublish the notification",
          icon: "error",
        });
        console.error(result.error);
      }
    } catch (error: any) {
      showToast({
        title: "Error",
        text: error.message || "Could not unpublish the notification",
        icon: "error",
      });
      console.error("Error unpublishing notification:", error);
    }
  };

  return (
    <>
      {/* Unified Delete Modal */}
      <div className="modal fade" id="delete-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <form onSubmit={handleDelete}>
              <div className="modal-body text-center">
                <span className="delete-icon">
                  <i className="ti ti-trash fs-24" />
                </span>
                <h4>{modalContent.title}</h4>
                <p className="mb-4">
                  {modalContent.message}
                  {deleteType === "attachment" &&
                    selectedAttachment &&
                    !isDeleteBulk && (
                      <>
                        <br />
                        <strong className="text-break">
                          {selectedAttachment as string}
                        </strong>
                      </>
                    )}
                  <br />
                  <small className="text-danger">
                    This action cannot be undone.
                  </small>
                </p>
                <div className="d-flex justify-content-center">
                  <Link
                    to="#"
                    className="btn btn-light me-3"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-danger"
                    data-bs-dismiss="modal"
                    disabled={modalContent.loading}
                  >
                    {modalContent.loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                        ></span>
                        Deleting...
                      </>
                    ) : (
                      `Yes, ${modalContent.buttonText}`
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Publish Modal */}
      <div className="modal fade" id="publish-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handlePublish();
              }}
            >
              <div className="modal-body text-center">
                <span className="publish-icon">
                  <i className="ti ti-send" />
                </span>
                <h4>Publish Notification</h4>
                <p>
                  Are you sure you want to publish this notification? This will
                  send the notification to all recipients.
                </p>
                <div className="d-flex justify-content-center">
                  <Link
                    to="#"
                    className="btn btn-light me-3"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-success"
                    data-bs-dismiss="modal"
                    disabled={isPublishing}
                  >
                    {isPublishing ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                        ></span>
                        Publishing...
                      </>
                    ) : (
                      "Yes, Publish"
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* /Publish Modal */}

      {/* Unpublish Modal */}
      <div className="modal fade" id="unpublish-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleUnpublish();
              }}
            >
              <div className="modal-body text-center">
                <span className="unpublish-icon">
                  <i className="ti ti-x" />
                </span>
                <h4>Unpublish Notification</h4>
                <p>
                  Are you sure you want to unpublish this notification? This
                  will hide the notification from all recipients.
                </p>
                <div className="d-flex justify-content-center">
                  <Link
                    to="#"
                    className="btn btn-light me-3"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-warning"
                    data-bs-dismiss="modal"
                    disabled={isPublishing}
                  >
                    {isPublishing ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                        ></span>
                        Unpublishing...
                      </>
                    ) : (
                      "Yes, Unpublish"
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* /Unpublish Modal */}

      {/* Preview Notification Modal */}
      <div className="modal fade" id="preview-modal">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Notification Preview</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="modal-body">
              <div className="notification-preview">
                <h4 id="preview-title">Notification Title</h4>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span
                    className="badge bg-light-primary text-primary"
                    id="preview-date"
                  >
                    Date
                  </span>
                  <span
                    className="badge bg-light-success text-success"
                    id="preview-status"
                  >
                    Published
                  </span>
                </div>
                <div className="mb-3" id="preview-content">
                  Notification content will appear here.
                </div>
                <div className="attachments mt-3" id="preview-attachments">
                  <h6>Attachments:</h6>
                  <div className="d-flex flex-wrap gap-2">
                    {/* Attachments will be dynamically added here */}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-light"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NotificationModals;
